import { type FC, useState, useEffect, useMemo } from "react";

import { ArrowRight, Calendar, Check, Lock, Minus, Phone, ReceiptText } from "lucide-react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { format } from "date-fns";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { enqueueSnackbar } from "notistack";

import MainLayout from "@/pages/layouts/MainLayout";
import { PageActions } from "@/pages/layouts/components/PageActions";
import { Button } from "@/shared/components/ui/button";
import { Progress } from "@/shared/components/ui/progress";
import { Callout } from "@/shared/components/ui/callout";
import { CalendlyModal } from "@/shared/components/overlay/CalendlyModal";
import { UploadDocumentModal } from "@/shared/components/overlay/UploadDocumentModal";
import { useAppContext } from "@/shared/hooks/useAppContext";
import { useUserEstateRole } from "@/shared/hooks/useUserEstateRole";
import { usePaymentTier } from "@/shared/hooks/usePaymentTier";
import { useCalendlyBooking } from "@/shared/hooks/useCalendlyBooking";
import { useSaveCalendlyBooking } from "@/shared/hooks/useSaveCalendlyBooking";
import { useEstateItems } from "@/shared/hooks/useEstateItems";
import { useHasAuthorityDocument } from "@/shared/hooks/useHasAuthorityDocument";
import { useDeceasedEINData } from "@/shared/hooks/useDeceasedEINData";
import { useModuleProgress } from "@/shared/hooks/useModuleProgress";
import { usePlaid } from "@/shared/hooks/usePlaid";
import { supabase } from "@/shared/lib/supabase";
import { hasMinTier } from "@/shared/utils/tierOrder";
import { SETTLE_TASK_IDS } from "@/shared/constants/taskIds";
import { cn } from "@/shared/utils/core";
import { formatCurrency } from "@/shared/utils/formatters";
import { CALENDLY_URLS, ESTATE_EXPERT_BOOKING_SOURCE } from "@/shared/constants/calendlyUrls";
import { POST_LOSS_ROUTES } from "@/shared/constants/paths";
import { DocumentType } from "@/shared/types/documentTypes";
import PostLossPaywall from "@/pages/postloss/components/PostLossPaywall";
import {
  Tooltip,
  TooltipContent,
  TooltipInfoTrigger,
  TooltipProvider,
} from "@/shared/components/ui/tooltip";
import { WHY_WE_ASK, WHY_WE_ASK_TITLE } from "@/shared/constants/whyWeAsk";

import { SETTLE_STEPS, SETTLE_SECTIONS, TOTAL_STEPS } from "./settleSteps";
import { SettleStepRow } from "./components/SettleStepRow";
import { SettleUpsellCard } from "./components/SettleUpsellCard";
import { GetEINDrawer } from "./components/GetEINDrawer";
import { AlreadyHaveEINDrawer } from "./components/AlreadyHaveEINDrawer";

import type { SettleStepStatus } from "./settleSteps";

const CALENDLY_SOURCE = "settle_estate";

export const SettleEstatePage: FC = () => {
  const { person, user } = useAppContext();
  const firstName = person?.first_name ?? "your loved one";
  const estateId = person?.estate_id;

  const {
    hasLegalAuthority: hasAuthority,
    isTrustee,
    legalRole,
    isLoading: isRoleLoading,
  } = useUserEstateRole();
  const { tier, isLoading: isTierLoading } = usePaymentTier(estateId);
  const isPlatinumUser = hasMinTier(tier, "platinum");

  const { hasAuthorityDocument: hasRequiredDoc, setHasAuthorityDocument } = useHasAuthorityDocument(
    estateId,
    legalRole,
  );

  const queryClient = useQueryClient();

  const [calendlyOpen, setCalendlyOpen] = useState<"step4" | "expert" | false>(false);
  const [paywallOpen, setPaywallOpen] = useState(false);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [autoTriggered, setAutoTriggered] = useState(false);
  const [einDrawer, setEinDrawer] = useState<"get" | "already" | false>(false);

  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Step 4 booking (settle-specific)
  const { booking } = useCalendlyBooking(estateId, CALENDLY_SOURCE);
  const saveBookingMutation = useSaveCalendlyBooking(estateId, CALENDLY_SOURCE);

  // Expert call booking (shared with Get Authorized and Overview)
  const { booking: expertBooking } = useCalendlyBooking(estateId, ESTATE_EXPERT_BOOKING_SOURCE);
  const saveExpertBookingMutation = useSaveCalendlyBooking(estateId, ESTATE_EXPERT_BOOKING_SOURCE);

  // Step 1-3 data
  const { hasEIN } = useDeceasedEINData(estateId);
  const { taskStatusMap, taskStartedAtMap } = useModuleProgress();
  const { connections } = usePlaid(estateId, "estate_account");
  const hasEstateAccount = connections.length > 0;

  const SETTLE_DOC_TYPES = [
    "ein_confirmation",
    "death_certificate",
    "letters_testamentary",
    "letters_administration",
  ];
  const { data: uploadedDocTypes = [] } = useQuery({
    queryKey: ["settleDocTypes", estateId],
    queryFn: async () => {
      const { data } = await supabase
        .from("estate_documents")
        .select("document_type")
        .eq("estate_id", estateId!)
        .in("document_type", SETTLE_DOC_TYPES);
      const types = new Set((data ?? []).map((d: { document_type: string }) => d.document_type));
      return [...types];
    },
    enabled: !!estateId,
  });
  const hasEINDocument = uploadedDocTypes.includes("ein_confirmation");
  const hasDeathCert = uploadedDocTypes.includes("death_certificate");
  const hasLettersOfAuthority =
    uploadedDocTypes.includes("letters_testamentary") ||
    uploadedDocTypes.includes("letters_administration");

  const { debts } = useEstateItems(estateId);
  const totalExpenses = useMemo(() => {
    return debts
      .filter((d) => d.category === "Ongoing Estate Expenses")
      .reduce((sum, d) => sum + Math.abs(d.balance ?? 0), 0);
  }, [debts]);

  useEffect(() => {
    if (autoTriggered || isTierLoading || isRoleLoading) return;
    if (searchParams.get("action") === "meet-expert") {
      setAutoTriggered(true);
      const next = new URLSearchParams(searchParams);
      next.delete("action");
      setSearchParams(next, { replace: true });
      if (!isPlatinumUser) {
        setPaywallOpen(true);
      } else if (hasAuthority) {
        setCalendlyOpen("step4");
      } else {
        setCalendlyOpen("expert");
      }
    }
  }, [
    autoTriggered,
    isTierLoading,
    isRoleLoading,
    isPlatinumUser,
    hasAuthority,
    searchParams,
    setSearchParams,
  ]);

  const handleEventScheduled = (eventUri: string, inviteeUri: string) => {
    if (calendlyOpen === "expert") {
      saveExpertBookingMutation.mutate({ eventUri, inviteeUri });
    } else {
      saveBookingMutation.mutate({ eventUri, inviteeUri });
    }
  };

  const requiredDocType = isTrustee ? DocumentType.TRUST : DocumentType.LETTERS_TESTAMENTARY;

  const uploadTitle = isTrustee ? "Upload Trust" : "Upload Letters of Authority";

  const handleUploadSuccess = () => {
    setHasAuthorityDocument();
    void queryClient.invalidateQueries({ queryKey: ["hasAuthorityDocument", estateId, legalRole] });
  };

  const handleMarkBankOpened = async () => {
    if (!estateId) return;
    try {
      const now = new Date().toISOString();
      const { error } = await supabase.from("estate_tasks").upsert(
        {
          estate_id: estateId,
          task_id: SETTLE_TASK_IDS.OPEN_BANK,
          status: "completed",
          completed_at: now,
          last_active_at: now,
        },
        { onConflict: "estate_id,task_id" },
      );
      if (error) throw error;
      await queryClient.invalidateQueries({ queryKey: ["moduleProgress", estateId] });
      enqueueSnackbar("Marked as done", { variant: "success" });
    } catch {
      enqueueSnackbar("Could not update. Please try again.", { variant: "error" });
    }
  };

  const isLoading = isRoleLoading || isTierLoading;
  const hasAuthorityDoc = hasLettersOfAuthority || hasRequiredDoc;
  const accessState: "not-paid" | "no-authority" | "needs-doc-upload" | "full" = !isPlatinumUser
    ? "not-paid"
    : hasAuthorityDoc
      ? "full"
      : !hasAuthority
        ? "no-authority"
        : "needs-doc-upload";

  const step1TaskStatus = taskStatusMap.get(SETTLE_TASK_IDS.GET_EIN);
  const step2TaskStatus = taskStatusMap.get(SETTLE_TASK_IDS.OPEN_BANK);

  useEffect(() => {
    if (!hasEIN || !estateId || step1TaskStatus !== "in_progress") return;
    const now = new Date().toISOString();
    supabase
      .from("estate_tasks")
      .upsert(
        {
          estate_id: estateId,
          task_id: SETTLE_TASK_IDS.GET_EIN,
          status: "completed",
          completed_at: now,
          last_active_at: now,
        },
        { onConflict: "estate_id,task_id" },
      )
      .then(() => queryClient.invalidateQueries({ queryKey: ["moduleProgress", estateId] }));
  }, [hasEIN, estateId, step1TaskStatus, queryClient]);

  const stepStatuses = useMemo((): SettleStepStatus[] => {
    if (accessState !== "full") {
      return SETTLE_STEPS.map(() => "locked" as const);
    }

    const step1: SettleStepStatus = hasEIN
      ? "complete"
      : step1TaskStatus === "in_progress"
        ? "waiting"
        : "active";

    const step2: SettleStepStatus =
      step2TaskStatus === "completed" ? "complete" : step1 === "complete" ? "active" : "upcoming";

    const step3: SettleStepStatus = hasEstateAccount
      ? "complete"
      : step2 === "complete"
        ? "active"
        : "upcoming";

    const allSection1Done = step1 === "complete" && step2 === "complete" && step3 === "complete";
    const step4: SettleStepStatus = allSection1Done ? "active" : "upcoming";

    return SETTLE_STEPS.map((step) => {
      if (step.id === "get-ein") return step1;
      if (step.id === "open-bank") return step2;
      if (step.id === "connect-bank") return step3;
      if (step.id === "release-accounts") return step4;
      return allSection1Done ? "upcoming" : "upcoming";
    });
  }, [accessState, hasEIN, step1TaskStatus, step2TaskStatus, hasEstateAccount]);

  const completedCount = stepStatuses.filter((s) => s === "complete" || s === "waiting").length;

  if (isLoading) {
    return (
      <MainLayout header="Settle the estate" actions={<PageActions />} className="flex flex-col">
        <div className="flex flex-1 items-center justify-center">
          <p className="text-sm text-secondary">Loading...</p>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout header="Settle the estate" actions={<PageActions />} className="flex flex-col">
      <div className="flex flex-col gap-10 p-6 sm:p-10">
        {/* Header */}
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:gap-10">
            <div className="flex flex-1 flex-col gap-1">
              <h4>Your path forward</h4>
              <p className="text-sm text-secondary">
                These steps settle {firstName}&apos;s estate, from opening a bank account in its
                name to giving each person what they inherit. Start with the estate&apos;s tax ID,
                since every step after it depends on it.
              </p>
            </div>
            <div className="flex shrink-0 items-center gap-4">
              <Progress
                value={(completedCount / TOTAL_STEPS) * 100}
                className="flex-1 sm:flex-none sm:w-[200px]"
              />
              <p className="whitespace-nowrap text-xs font-medium text-tertiary">
                {completedCount} of {TOTAL_STEPS} done
              </p>
            </div>
          </div>
          {accessState === "full" ? (
            <div className="flex items-center gap-2">
              <Phone className="h-4 w-4 text-secondary" />
              <p className="text-sm text-secondary">
                Questions?{" "}
                <Button
                  variant="link"
                  size={null}
                  className="text-sm text-brand underline"
                  onClick={() => setCalendlyOpen("expert")}
                >
                  Book a session with your Estate Expert
                </Button>
              </p>
            </div>
          ) : null}
        </div>

        {accessState === "not-paid" ? (
          <SettleUpsellCard
            currentTier={tier}
            onUpgrade={() => setPaywallOpen(true)}
            isLoading={isTierLoading}
          />
        ) : null}

        {accessState === "no-authority" ? (
          <NoAuthorityBanner
            onGetAuthorized={() => navigate(POST_LOSS_ROUTES.GET_AUTHORIZED)}
            onAddReceipts={() => navigate(POST_LOSS_ROUTES.ESTATE_INVENTORY)}
            onBookSession={() => setCalendlyOpen("expert")}
            expertBooking={expertBooking}
          />
        ) : null}

        {accessState === "needs-doc-upload" ? (
          <NeedsDocUploadBanner
            onUpload={() => setUploadModalOpen(true)}
            onAddReceipts={() => navigate(POST_LOSS_ROUTES.ESTATE_INVENTORY)}
            onBookSession={() => setCalendlyOpen("expert")}
            expertBooking={expertBooking}
          />
        ) : null}

        {accessState === "full" && totalExpenses > 0 ? (
          <Callout
            variant="brand"
            title={`The estate may owe you ${formatCurrency(totalExpenses)}`}
            trailing={
              <Link
                to={POST_LOSS_ROUTES.ESTATE_INVENTORY}
                className="text-sm font-medium text-brand underline"
              >
                See the expenses
              </Link>
            }
          >
            That&apos;s the eligible estate expenses on {firstName}&apos;s record. Once the estate
            bank account is connected, step 5 shows you how to pay yourself back.
          </Callout>
        ) : null}

        {SETTLE_SECTIONS.map((section) => {
          const sectionSteps = SETTLE_STEPS.map((step, i) => ({ ...step, index: i })).filter(
            (s) => s.section === section.key,
          );

          return (
            <div
              key={section.key}
              className={cn(
                "overflow-hidden rounded-lg border border-primary bg-white",
                accessState !== "full" && "opacity-50",
              )}
            >
              <div className="flex flex-col gap-1 px-4 py-4 sm:px-6">
                <p className="text-base font-semibold">{section.title}</p>
                <p className="text-sm text-secondary">{section.getDescription(firstName)}</p>
              </div>
              <div className="flex flex-col">
                {sectionSteps.map((step) => {
                  const status = stepStatuses[step.index];

                  const stepNumber = step.index + 1;

                  // Step 1: EIN - active card with two CTAs, waiting with IRS date, complete with "View IRS Letter"
                  if (step.id === "get-ein") {
                    const ssnSubmittedAt = taskStartedAtMap.get(SETTLE_TASK_IDS.GET_EIN);
                    return (
                      <SettleStepRow
                        key={step.id}
                        stepNumber={stepNumber}
                        title={step.getTitle(firstName)}
                        status={status}
                        description={
                          status === "active"
                            ? "The IRS issues the estate its own tax ID, and banks ask for it to open an estate account. Takes about 2 business days."
                            : undefined
                        }
                        cta={
                          status === "active"
                            ? { label: "Get tax ID", onClick: () => setEinDrawer("get") }
                            : undefined
                        }
                        secondaryLink={
                          status === "active"
                            ? {
                                label: "I already have one",
                                onClick: () => setEinDrawer("already"),
                              }
                            : status === "complete" && hasEINDocument
                              ? {
                                  label: "View IRS Letter",
                                  onClick: () => navigate(POST_LOSS_ROUTES.DOCUMENTS),
                                }
                              : undefined
                        }
                        waitingLabel={status === "waiting" ? "Waiting on the IRS" : undefined}
                        waitingDescription={
                          status === "waiting" && ssnSubmittedAt
                            ? `Elayne sent the application to the IRS on ${format(new Date(ssnSubmittedAt), "MMM d")}. We'll email you and add the EIN here when it arrives.`
                            : status === "waiting"
                              ? "Elayne sent the application to the IRS. We'll email you and add the EIN here when it arrives."
                              : undefined
                        }
                      />
                    );
                  }

                  if (step.id === "release-accounts" && status === "active") {
                    return (
                      <SettleStepRow
                        key={step.id}
                        stepNumber={stepNumber}
                        title={step.getTitle(firstName)}
                        status={status}
                        description={step.getExpandDescription?.(firstName)}
                      >
                        <Step4BookingCard
                          booking={booking}
                          onBook={() => setCalendlyOpen("step4")}
                        />
                      </SettleStepRow>
                    );
                  }

                  if (step.id === "open-bank" && status === "active") {
                    return (
                      <SettleStepRow
                        key={step.id}
                        stepNumber={stepNumber}
                        title={step.getTitle(firstName)}
                        status={status}
                        description={`Open a checking account in the estate's name at any bank. Estate money goes in here, and you pay bills and yourself back from it. Bring these with you:`}
                        cta={{ label: "I opened the account", onClick: handleMarkBankOpened }}
                      >
                        <Step2DocumentChecklist
                          hasEINDoc={hasEINDocument}
                          hasLetters={hasLettersOfAuthority}
                          hasDeathCert={hasDeathCert}
                          firstName={firstName}
                          onViewDocument={() => navigate(POST_LOSS_ROUTES.DOCUMENTS)}
                        />
                      </SettleStepRow>
                    );
                  }

                  // Step 3: Connect bank - active card with Plaid CTA (Phase 4)
                  if (step.id === "connect-bank") {
                    return (
                      <SettleStepRow
                        key={step.id}
                        stepNumber={stepNumber}
                        title={step.getTitle(firstName)}
                        status={status}
                        description={
                          status === "active"
                            ? "Link the estate bank account so Elayne can track what comes in and goes out."
                            : undefined
                        }
                        cta={
                          status === "active"
                            ? {
                                label: "Connect the account",
                                onClick: () =>
                                  navigate(
                                    `${POST_LOSS_ROUTES.ESTATE_INVENTORY}?tab=estate-account`,
                                  ),
                              }
                            : undefined
                        }
                        secondaryLink={
                          status === "complete"
                            ? {
                                label: "View account",
                                onClick: () =>
                                  navigate(
                                    `${POST_LOSS_ROUTES.ESTATE_INVENTORY}?tab=estate-account`,
                                  ),
                              }
                            : undefined
                        }
                      />
                    );
                  }

                  if (step.id === "pay-self") {
                    return (
                      <SettleStepRow
                        key={step.id}
                        stepNumber={stepNumber}
                        title={step.getTitle(firstName)}
                        status={status}
                        expandContent={accessState === "full" ? <Step5ReceiptsNudge /> : undefined}
                        alwaysShowContent={accessState === "full"}
                      />
                    );
                  }

                  return (
                    <SettleStepRow
                      key={step.id}
                      stepNumber={stepNumber}
                      title={step.getTitle(firstName)}
                      status={status}
                      description={
                        accessState === "full" ? step.getExpandDescription?.(firstName) : undefined
                      }
                    />
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      <CalendlyModal
        isOpen={!!calendlyOpen}
        onClose={() => setCalendlyOpen(false)}
        url={CALENDLY_URLS.ESTATE_SETTLEMENT_QUOTE}
        onEventScheduled={handleEventScheduled}
      />

      <PostLossPaywall
        isOpen={paywallOpen}
        onOpenChange={setPaywallOpen}
        promptLocation="settle_estate"
        currentTier={tier}
        returnPath={POST_LOSS_ROUTES.SETTLE_ESTATE}
      />

      {estateId ? (
        <>
          <UploadDocumentModal
            isOpen={uploadModalOpen}
            onClose={() => setUploadModalOpen(false)}
            estateId={estateId}
            documentType={requiredDocType}
            title={uploadTitle}
            size="lg"
            onSuccess={handleUploadSuccess}
          />
          <GetEINDrawer
            isOpen={einDrawer === "get"}
            onClose={() => setEinDrawer(false)}
            estateId={estateId}
            userId={user?.user_id ?? 0}
            userEmail={user?.email ?? ""}
            firstName={firstName}
          />
          <AlreadyHaveEINDrawer
            isOpen={einDrawer === "already"}
            onClose={() => setEinDrawer(false)}
            estateId={estateId}
          />
        </>
      ) : null}
    </MainLayout>
  );
};

interface Step4BookingCardProps {
  booking: { start_time: string | null; event_name: string | null } | null | undefined;
  onBook: () => void;
}

const Step4BookingCard: FC<Step4BookingCardProps> = ({ booking, onBook }) => {
  if (booking) {
    return (
      <div className="rounded-md border border-primary bg-white p-4">
        <p className="text-sm font-medium">
          {booking.start_time
            ? format(new Date(booking.start_time), "EEEE, MMMM d 'at' h:mm a")
            : "Call scheduled"}
        </p>
        {booking.event_name ? (
          <p className="mt-1 text-xs text-secondary">{booking.event_name}</p>
        ) : null}
      </div>
    );
  }

  return (
    <>
      <div className="flex flex-col gap-3 rounded-lg border border-primary bg-white p-4 sm:flex-row sm:items-center sm:gap-4">
        <div className="flex items-center gap-3 flex-1">
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-sm border border-primary bg-white">
            <Phone className="h-4 w-4 text-secondary" />
          </div>
          <div className="flex flex-1 flex-col gap-1">
            <p className="text-sm font-semibold">Have a call with an Estate Expert</p>
            <p className="text-xs text-secondary">30 minutes</p>
          </div>
        </div>
        <Button variant="brand" className="self-start" onClick={onBook}>
          Book a call
          <ArrowRight className="ml-1 h-4 w-4" />
        </Button>
      </div>
      <p className="text-xs text-secondary">Included in Estate Settlement</p>
    </>
  );
};

interface Step2Doc {
  label: string;
  type: "document" | "bring";
  uploaded: boolean;
  whyWeAsk?: string;
}

interface Step2DocumentChecklistProps {
  hasEINDoc: boolean;
  hasLetters: boolean;
  hasDeathCert: boolean;
  firstName: string;
  onViewDocument: () => void;
}

const Step2DocumentChecklist: FC<Step2DocumentChecklistProps> = ({
  hasEINDoc,
  hasLetters,
  hasDeathCert,
  firstName,
  onViewDocument,
}) => {
  const docs: Step2Doc[] = [
    {
      label: "IRS letter with the estate's EIN",
      type: "document",
      uploaded: hasEINDoc,
      whyWeAsk: WHY_WE_ASK.einConfirmation(firstName),
    },
    {
      label: "Your legal authority (letters of administration)",
      type: "document",
      uploaded: hasLetters,
    },
    { label: "A certified death certificate", type: "document", uploaded: hasDeathCert },
    { label: "Your driver's license or passport", type: "bring", uploaded: false },
  ];

  return (
    <div className="flex flex-col overflow-hidden rounded-lg border border-primary bg-white">
      {docs.map((doc) => (
        <div
          key={doc.label}
          className="flex items-center gap-2 border-b border-primary px-3 py-3 last:border-b-0 sm:px-4"
        >
          {doc.type === "document" && doc.uploaded ? (
            <Check className="h-4 w-4 shrink-0 text-brand" strokeWidth={2.5} />
          ) : (
            <Minus className="h-4 w-4 shrink-0 text-tertiary" />
          )}
          <p className="flex-1 text-xs">
            {doc.label}
            {doc.whyWeAsk && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipInfoTrigger aria-label={WHY_WE_ASK_TITLE} className="ml-1 align-middle" />
                  <TooltipContent title={WHY_WE_ASK_TITLE}>{doc.whyWeAsk}</TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </p>
          {doc.type === "document" ? (
            doc.uploaded ? (
              <button onClick={onViewDocument} className="shrink-0 text-sm font-medium underline">
                View
              </button>
            ) : null
          ) : (
            <span className="shrink-0 text-xs text-tertiary">Bring it with you</span>
          )}
        </div>
      ))}
    </div>
  );
};

const Step5ReceiptsNudge: FC = () => (
  <div className="flex flex-col gap-2 rounded-sm border border-primary bg-subtle px-4 py-3 sm:flex-row sm:items-center sm:gap-3">
    <div className="flex flex-1 items-start gap-3">
      <ReceiptText className="h-4 w-4 shrink-0 text-secondary mt-0.5 sm:mt-0" />
      <p className="flex-1 text-xs">
        Keep adding receipts while you wait, so the estate can pay you back as soon as money comes
        in
      </p>
    </div>
    <Link
      to={POST_LOSS_ROUTES.ESTATE_INVENTORY}
      className="shrink-0 text-sm font-medium text-brand underline"
    >
      Add receipts
    </Link>
  </div>
);

// -- No-authority banner --

interface NoAuthorityBannerProps {
  onGetAuthorized: () => void;
  onAddReceipts: () => void;
  onBookSession: () => void;
  expertBooking: { start_time: string | null; event_name: string | null } | null;
}

const NoAuthorityBanner: FC<NoAuthorityBannerProps> = ({
  onGetAuthorized,
  onAddReceipts,
  onBookSession,
  expertBooking,
}) => (
  <div className="flex flex-col gap-4 rounded-lg border border-alert bg-yellow-50 p-4">
    <div className="flex flex-col gap-3 sm:flex-row sm:items-start">
      <div className="flex items-start gap-3 flex-1">
        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-sm border border-primary bg-white">
          <Lock className="h-4 w-4 text-secondary" />
        </div>
        <div className="flex flex-1 flex-col">
          <p className="text-sm font-semibold">These steps open once the court appoints you</p>
          <p className="text-xs text-secondary">
            Banks need your legal authority first, but 2 things can start now.
          </p>
        </div>
      </div>
      <Button variant="brand" className="shrink-0 self-start" onClick={onGetAuthorized}>
        Continue getting authorized
        <ArrowRight className="ml-1 h-4 w-4" />
      </Button>
    </div>
    <div className="flex flex-col gap-3 sm:pl-10">
      <div className="flex items-center gap-3 rounded-sm border border-primary bg-white px-4 py-3">
        <ReceiptText className="h-4 w-4 shrink-0 text-secondary" />
        <div className="flex flex-1 flex-col">
          <p className="text-xs font-medium">Save your receipts</p>
          <p className="text-xs">The estate pays you back once money comes in</p>
        </div>
        <Button
          variant="link"
          size={null}
          onClick={onAddReceipts}
          className="shrink-0 text-sm font-medium text-brand underline"
        >
          Add receipts
        </Button>
      </div>
      {expertBooking ? (
        <div className="flex items-center gap-4 rounded-lg border border-primary bg-white p-4">
          <Calendar className="h-5 w-5 shrink-0 text-secondary" />
          <div className="flex flex-1 flex-col gap-1">
            <p className="text-sm font-medium">
              {expertBooking.start_time
                ? format(new Date(expertBooking.start_time), "EEEE, MMMM d 'at' h:mm a")
                : "Call scheduled"}
            </p>
            <p className="text-xs text-secondary">
              {expertBooking.event_name ?? "30 minutes with your Estate Expert"}
            </p>
          </div>
        </div>
      ) : (
        <div className="flex items-center gap-3 rounded-sm border border-primary bg-white px-4 py-3">
          <Phone className="h-4 w-4 shrink-0 text-secondary" />
          <div className="flex flex-1 flex-col">
            <p className="text-xs font-medium">Talk to your Estate Expert</p>
            <p className="text-xs">Ask what comes next while you wait for the court</p>
          </div>
          <Button
            variant="link"
            size={null}
            onClick={onBookSession}
            className="shrink-0 text-sm font-medium text-brand underline"
          >
            Book a session
          </Button>
        </div>
      )}
    </div>
  </div>
);

// -- Needs-doc-upload banner --

interface NeedsDocUploadBannerProps {
  onUpload: () => void;
  onAddReceipts: () => void;
  onBookSession: () => void;
  expertBooking: { start_time: string | null; event_name: string | null } | null;
}

const NeedsDocUploadBanner: FC<NeedsDocUploadBannerProps> = ({
  onUpload,
  onAddReceipts,
  onBookSession,
  expertBooking,
}) => (
  <div className="flex flex-col gap-4 rounded-lg border border-alert bg-yellow-50 p-4">
    <div className="flex items-start gap-3">
      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-sm border border-primary bg-white">
        <Lock className="h-4 w-4 text-secondary" />
      </div>
      <div className="flex flex-1 flex-col">
        <p className="text-sm font-semibold">These steps open once the court appoints you</p>
        <p className="text-xs text-secondary">
          Banks need your legal authority first, but 2 things can start now.
        </p>
      </div>
      <Button variant="brand" className="shrink-0" onClick={onUpload}>
        Upload legal authority
        <ArrowRight className="ml-1 h-4 w-4" />
      </Button>
    </div>
    <div className="flex flex-col gap-3 pl-10">
      <div className="flex items-center gap-3 rounded-sm border border-primary bg-white px-4 py-3">
        <ReceiptText className="h-4 w-4 shrink-0 text-secondary" />
        <div className="flex flex-1 flex-col">
          <p className="text-xs font-medium">Save your receipts</p>
          <p className="text-xs">The estate pays you back once money comes in</p>
        </div>
        <Button
          variant="link"
          size={null}
          onClick={onAddReceipts}
          className="shrink-0 text-sm font-medium text-brand underline"
        >
          Add receipts
        </Button>
      </div>
      {expertBooking ? (
        <div className="flex items-center gap-4 rounded-lg border border-primary bg-white p-4">
          <Calendar className="h-5 w-5 shrink-0 text-secondary" />
          <div className="flex flex-1 flex-col gap-1">
            <p className="text-sm font-medium">
              {expertBooking.start_time
                ? format(new Date(expertBooking.start_time), "EEEE, MMMM d 'at' h:mm a")
                : "Call scheduled"}
            </p>
            <p className="text-xs text-secondary">
              {expertBooking.event_name ?? "30 minutes with your Estate Expert"}
            </p>
          </div>
        </div>
      ) : (
        <div className="flex items-center gap-3 rounded-sm border border-primary bg-white px-4 py-3">
          <Phone className="h-4 w-4 shrink-0 text-secondary" />
          <div className="flex flex-1 flex-col">
            <p className="text-xs font-medium">Talk to your Estate Expert</p>
            <p className="text-xs">Ask what comes next while you wait for the court</p>
          </div>
          <Button
            variant="link"
            size={null}
            onClick={onBookSession}
            className="shrink-0 text-sm font-medium text-brand underline"
          >
            Book a session
          </Button>
        </div>
      )}
    </div>
  </div>
);
